import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core';

export interface ParcelToInvalid {
  parcelCode: string;
  parcelInactiveForLoadingLists: boolean;
}

export type LoadingListsAction = 'invalidate' | 'validate';

@Component({
  selector: 'app-invalidate-loading-lists-popup',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './invalidate-loading-lists-popup.component.html',
  styleUrl: './invalidate-loading-lists-popup.component.css'
})
export class InvalidateLoadingListsPopupComponent implements OnChanges {
  @Input() visible: boolean = false;
  @Input() parcels: ParcelToInvalid[] = [];
  @Input() isSubmitting: boolean = false;

  /**
   * tab iniziale:
   * - invalidate => mostra i validi da invalidare
   * - validate   => mostra gli invalidi da rivalidare
   */
  @Input() initialTab: LoadingListsAction = 'invalidate';

  @Output() closed = new EventEmitter<void>();
  @Output() confirmed = new EventEmitter<{
    action: LoadingListsAction;
    parcelCodes: string[];
  }>();

  selectedTab: LoadingListsAction = 'invalidate';
  localParcels: ParcelToInvalid[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.selectedTab = this.initialTab;
      this.syncLocalParcels();
    }

    if (changes['parcels'] && this.visible) {
      this.syncLocalParcels();
    }
  }

  private syncLocalParcels(): void {
    this.localParcels = [...this.getParcelsForTab(this.selectedTab)];
  }

  setTab(tab: LoadingListsAction): void {
    if (this.isSubmitting) return;
    this.selectedTab = tab;
    this.syncLocalParcels();
  }

  get invalidableParcels(): ParcelToInvalid[] {
    return this.parcels.filter(parcel => !parcel.parcelInactiveForLoadingLists);
  }

  get validableParcels(): ParcelToInvalid[] {
    return this.parcels.filter(parcel => parcel.parcelInactiveForLoadingLists);
  }

  get currentTabParcels(): ParcelToInvalid[] {
    return this.selectedTab === 'invalidate'
      ? this.invalidableParcels
      : this.validableParcels;
  }

  get currentTitle(): string {
    return this.selectedTab === 'invalidate'
      ? 'Invalida colli nei Borderò'
      : 'Rivalida colli nei Borderò';
  }

  get currentDescription(): string {
    return this.selectedTab === 'invalidate'
      ? 'Colli validi che possono essere dichiarati invalidi'
      : 'Colli invalidi che possono essere ridichiarati validi';
  }

  get currentButtonLabel(): string {
    if (this.isSubmitting) {
      return this.selectedTab === 'invalidate' ? 'Invalidazione...' : 'Rivalidazione...';
    }

    return this.selectedTab === 'invalidate' ? 'Invalida' : 'Rivalida';
  }

  get currentButtonIcon(): string {
    if (this.isSubmitting) {
      return 'fas fa-spinner fa-spin me-2';
    }

    return this.selectedTab === 'invalidate'
      ? 'fas fa-ban me-2'
      : 'fas fa-undo me-2';
  }

  get currentButtonClass(): string {
    return this.selectedTab === 'invalidate' ? 'btn btn-danger' : 'btn btn-loading-lists-success';
  }

  get badgeClass(): string {
    return this.selectedTab === 'invalidate' ? 'bg-danger' : 'bg-loading-lists-success';
  }

  get sectionIcon(): string {
    return this.selectedTab === 'invalidate'
      ? 'fas fa-boxes me-2 text-danger'
      : 'fas fa-boxes me-2 text-loading-lists-success';
  }

  get emptyMessage(): string {
    return this.selectedTab === 'invalidate'
      ? 'Nessun collo valido disponibile da invalidare'
      : 'Nessun collo invalido disponibile da rivalidare';
  }

  get hasItems(): boolean {
    return this.localParcels.length > 0;
  }

  private getParcelsForTab(tab: LoadingListsAction): ParcelToInvalid[] {
    return tab === 'invalidate'
      ? this.invalidableParcels
      : this.validableParcels;
  }

  close(): void {
    if (this.isSubmitting) return;
    this.closed.emit();
  }

  onBackdropClick(event: Event): void {
    if (event.target === event.currentTarget && !this.isSubmitting) {
      this.close();
    }
  }

  onConfirm(): void {
    if (!this.hasItems || this.isSubmitting) {
      return;
    }

    this.confirmed.emit({
      action: this.selectedTab,
      parcelCodes: this.localParcels.map(parcel => parcel.parcelCode)
    });
  }

  removeParcel(parcelCode: string): void {
    if (this.isSubmitting) return;
    this.localParcels = this.localParcels.filter(parcel => parcel.parcelCode !== parcelCode);
  }

  trackByParcelCode(index: number, parcel: ParcelToInvalid): string {
    return parcel.parcelCode;
  }
}